import {
  CognitoError,
  type CognitoPool,
  confirmForgotPassword,
  confirmSignUp,
  forgotPassword,
  refresh,
  resendConfirmation,
  revoke,
  signInWithIdToken,
  signUp,
  signIn as srpSignIn,
  type Tokens,
} from '@going-once/client/cognito';

import { runtimeConfig } from './config';

/**
 * Sign-in against the user pool, with our own screen and SRP (D35 addendum, 2026-10-07).
 *
 * **Managed login is gone.** The web used Cognito's hosted page (authorization code with
 * PKCE) after the phone had moved off it; this is the phone's flow (`packages/client/src/
 * cognito.ts`, D52 addendum) behind a sheet of our own. Nothing about the tokens changes:
 * the same pool, the same pre-token trigger, the same claims, so the API cannot tell.
 *
 * **The access token is the one that matters.** Our claims — `userId`, `sellerId`, the
 * scope bundle — are put on the *access* token by the V2 pre-token trigger, and the API
 * verifies that. The id token is a user-profile artefact and is not sent anywhere.
 *
 * **Tokens live in memory, plus a refresh token in `localStorage`.** An access token in
 * `localStorage` is readable by any script that gets injected; keeping it in a module
 * variable means a reload costs a silent refresh rather than a sign-in. The refresh
 * token is the deliberate trade, and this is an internal tool (O32), not a customer
 * surface.
 *
 * **Two app clients** (GO-568). Staff sign in with Google through the staff client, the
 * only one whose tokens carry a staff role; everyone else uses the web client with a
 * password. A refresh token belongs to the client that issued it, so the client is stored
 * beside it, and a refresh token stored before there was a choice is the web client's.
 */

const REFRESH_KEY = 'go.refresh';
const CLIENT_KEY = 'go.refresh.client';

type ClientKind = 'web' | 'staff';
/** Refreshed a little early, because a token that expires mid-request is a 401. */
const REFRESH_MARGIN_MS = 60_000;

export interface Session {
  accessToken: string;
  /** Epoch ms. */
  expiresAt: number;
  claims: Record<string, unknown>;
}

let session: Session | undefined;

function storedClient(): ClientKind {
  return localStorage.getItem(CLIENT_KEY) === 'staff' ? 'staff' : 'web';
}

function pool(kind: ClientKind = storedClient()): CognitoPool {
  const { userPoolId, webClientId, staffSignIn } = runtimeConfig();
  // A staff session in an environment that has since lost its staff client cannot refresh;
  // Cognito refuses the web client's id with it, and `restore` signs the person out.
  const clientId = kind === 'staff' && staffSignIn ? staffSignIn.staffClientId : webClientId;
  return {
    userPoolId,
    clientId,
    crypto: {
      sha256: async (data) => new Uint8Array(await crypto.subtle.digest('SHA-256', data)),
      randomBytes: (length) => crypto.getRandomValues(new Uint8Array(length)),
    },
  };
}

export function decodeClaims(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  if (!payload) return {};
  try {
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function adopt(tokens: Tokens, kind?: ClientKind): Session {
  session = {
    accessToken: tokens.accessToken,
    expiresAt: Date.now() + tokens.expiresIn * 1000,
    claims: decodeClaims(tokens.accessToken),
  };
  // A refresh answers without one: Cognito keeps the token already held.
  if (tokens.refreshToken) localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  // Only a fresh sign-in names its client; a refresh keeps the one it was issued by.
  if (kind) localStorage.setItem(CLIENT_KEY, kind);
  return session;
}

/**
 * **`signIn()` keeps its old shape**: every screen that offers sign-in calls it, and none
 * of them should know that sign-in became a sheet rather than a redirect. It asks the
 * shell to open the sheet (`SignInSheet`) and resolves when the sheet closes, true when
 * somebody signed in. The shell then reloads, which is what the redirect used to do, so
 * every screen renders against the new claims without each one subscribing to them.
 */
type Opener = (open: boolean) => void;
let opener: Opener | undefined;
let pending: ((signedIn: boolean) => void) | undefined;

/** The shell registers the sheet it renders. One shell, so one opener. */
export function onSignInRequest(open: Opener): () => void {
  opener = open;
  return () => {
    if (opener === open) opener = undefined;
  };
}

export function signIn(): Promise<boolean> {
  // A second request while one is open answers the first as dismissed rather than
  // leaving a caller waiting on a promise nothing will ever settle.
  pending?.(false);
  return new Promise((resolve) => {
    pending = resolve;
    opener?.(true);
  });
}

/** Called by the sheet: true after a sign-in, false as it closes without one. */
export function settleSignIn(signedIn: boolean): void {
  const resolve = pending;
  pending = undefined;
  opener?.(false);
  resolve?.(signedIn);
}

/** What the password step can come back with. */
export type PasswordResult =
  | { status: 'signed-in'; session: Session }
  /** An authenticator app is enrolled; `submit` takes its six digits. */
  | { status: 'totp'; submit: (code: string) => Promise<Session> };

export async function signInWithPassword(email: string, password: string): Promise<PasswordResult> {
  const result = await srpSignIn(pool('web'), { email, password });
  if (result.status === 'signed-in') return { status: 'signed-in', session: adopt(result.tokens, 'web') };
  return { status: 'totp', submit: async (code) => adopt(await result.submit(code), 'web') };
}

/** Whether this environment offers staff Google sign-in, and with which Google client. */
export function googleClientId(): string | undefined {
  return runtimeConfig().staffSignIn?.googleClientId;
}

/**
 * Staff sign-in with the ID token Google's button handed back (GO-568). The account is the
 * token's own address; whether it may sign in is the pool's trigger to decide.
 *
 * **The first one creates the account.** Cognito answers an address it has no account for
 * with `NotAuthorizedException` before any challenge, so on that answer this signs up on the
 * staff client with the same token in the client metadata, and pre-sign-up confirms the
 * account if the token is a Workspace one on an allowed domain (`apps/auth/src/staff-provision.ts`).
 * The account holds no staff role until somebody runs `npm run staff -- add`.
 *
 * The password is random and kept nowhere: the staff client has no password flow, and a
 * password the person sets later through "Forgot password?" signs them in as an ordinary
 * account, never as staff.
 */
export async function signInWithGoogle(credential: string): Promise<Session> {
  const claims = decodeClaims(credential);
  const email = claims.email;
  if (typeof email !== 'string') throw new Error('Google returned no email address');
  const staff = pool('staff');
  try {
    return adopt(await signInWithIdToken(staff, { email, idToken: credential }), 'staff');
  } catch (refused) {
    if (!(refused instanceof CognitoError) || refused.code !== 'NotAuthorizedException') throw refused;
    try {
      await signUp(staff, {
        email,
        password: unusablePassword(),
        ...(typeof claims.name === 'string' ? { name: claims.name } : {}),
        clientMetadata: { [GOOGLE_ID_TOKEN_METADATA]: credential },
      });
    } catch (error) {
      // An account exists and refused the token for another reason (disabled, unverified):
      // the sign-in's answer is the one to show.
      if (error instanceof CognitoError && error.code === 'UsernameExistsException') throw refused;
      throw error;
    }
    return adopt(await signInWithIdToken(staff, { email, idToken: credential }), 'staff');
  }
}

/** Must match `apps/auth/src/staff-provision.ts`, which reads it. */
const GOOGLE_ID_TOKEN_METADATA = 'googleIdToken';

/** 64 hex characters and a letter the pool's policy requires, from the browser's CSPRNG. */
function unusablePassword(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return `g${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

// Account management is the password client's: the staff client has no password at all.
export const createAccount = (email: string, password: string): Promise<{ confirmed: boolean }> =>
  signUp(pool('web'), { email, password });

export const confirmAccount = (email: string, code: string): Promise<void> =>
  confirmSignUp(pool('web'), { email, code });

export const resendCode = (email: string): Promise<void> => resendConfirmation(pool('web'), { email });

export const startPasswordReset = (email: string): Promise<void> => forgotPassword(pool('web'), { email });

export const finishPasswordReset = (email: string, code: string, password: string): Promise<void> =>
  confirmForgotPassword(pool('web'), { email, code, password });

/**
 * Sign out here, then revoke at Cognito, then start again from the feed.
 *
 * **The local half happens first**, so a failed network call cannot leave anyone signed
 * in. Revoking means a refresh token copied out of `localStorage` can mint nothing more;
 * an access token already issued stays valid until it expires (at most an hour), because
 * the API verifies locally rather than asking Cognito on every request.
 */
export async function signOut({ stay = false }: { stay?: boolean } = {}): Promise<void> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  const issuer = pool();
  session = undefined;
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(CLIENT_KEY);
  if (refreshToken) {
    try {
      await revoke(issuer, refreshToken);
    } catch {
      // Already signed out here. A token that could not be revoked expires on its own.
    }
  }
  // `stay` is for a caller that opens the sign-in sheet next: navigating would drop it.
  if (!stay) window.location.assign('/');
}

/** Restore a session from the stored refresh token, if there is one. */
export async function restore(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return false;
  try {
    adopt(await refresh(pool(), refreshToken));
    return true;
  } catch (error) {
    session = undefined;
    // **Only Cognito's refusal signs a person out.** A revoked or expired refresh token is
    // a `CognitoError`, and keeping it would retry a dead token on every request. A dropped
    // connection is not, and deleting the token over one signs somebody out at random.
    if (error instanceof CognitoError) {
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(CLIENT_KEY);
    }
    return false;
  }
}

/**
 * A fresh access token, or undefined for a guest.
 *
 * Refreshed early. Claims are only as fresh as the token, which is the staleness the
 * whole system has to live with: a seller who just onboarded has no `sellerId` until the
 * next one — see `refreshSession`.
 */
export async function currentToken(): Promise<string | undefined> {
  if (session && session.expiresAt - REFRESH_MARGIN_MS > Date.now()) return session.accessToken;
  if (await restore()) return session?.accessToken;
  return undefined;
}

export function currentClaims(): Record<string, unknown> | undefined {
  return session?.claims;
}

export function isSignedIn(): boolean {
  return session !== undefined;
}

/**
 * Whether this session came from the staff client (GO-568). A password session never
 * carries a staff role, so the admin's refusal offers Google instead of a refresh.
 */
export function isStaffSession(): boolean {
  return session !== undefined && storedClient() === 'staff';
}

/**
 * Force a new access token.
 *
 * The "refresh session" step the design record has been calling for: after seller
 * onboarding the account has a `sellerId` and the token in hand does not, so every
 * seller capability refuses a user who has just become a seller.
 */
export async function refreshSession(): Promise<boolean> {
  session = undefined;
  return restore();
}
